/**
 * Enumerações canônicas do domínio eQMS.
 *
 * Substituem os `CHAR(1)` do legado ('A','P','R','V','D') por nomes explícitos.
 * O mapeamento legado→novo vive em `legacy-map.ts` e é usado exclusivamente pelo ETL.
 *
 * Referência: docs/legacy-spec/ged.md §3, plano-migracao-sgq-ged.md §7.1
 */
// ─────────────────────────────────────────────────────────────────────────────
// Documento / GED
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Estado da VERSÃO do documento (não do documento).
 *
 * Correção C8 em relação ao legado: `EXPIRED` é **materializado** por job noturno,
 * não derivado em cada query. No legado 'V' nunca era persistido — era um CASE
 * em SQL replicado em 5 lugares com 3 variações divergentes (ged.md §9.3).
 */
export const DocumentVersionStatus = {
    /** Rascunho — ainda não submetido a aprovação. Editável livremente, sem assinatura. */
    DRAFT: 'DRAFT',
    /** Submetido; aguardando decisão dos aprovadores. Legado: 'P' */
    IN_REVIEW: 'IN_REVIEW',
    /** Aprovado e vigente. Legado: 'A' */
    APPROVED: 'APPROVED',
    /** Reprovado por ao menos um aprovador. Legado: 'R' */
    REJECTED: 'REJECTED',
    /** Vigência expirada (validade vencida). Materializado por job. Legado: 'V' (derivado) */
    EXPIRED: 'EXPIRED',
    /** Substituído por versão posterior. Legado: `ies_versao = 0` */
    SUPERSEDED: 'SUPERSEDED',
    /** Descontinuado deliberadamente. Legado: 'D' */
    OBSOLETE: 'OBSOLETE',
};
/**
 * Política de aprovação por documento — corrige C9.
 *
 * No legado a primeira decisão promovia a versão e cada nova sobrescrevia a anterior
 * (ged.md §7.3), apesar das colunas `last_decision*` sugerirem quórum.
 */
export const ApprovalPolicy = {
    /** Uma aprovação basta. Equivale ao comportamento de fato do legado. */
    ANY_ONE: 'ANY_ONE',
    /** Todos os aprovadores designados precisam aprovar. */
    ALL_REQUIRED: 'ALL_REQUIRED',
    /** N aprovações de um conjunto maior. `quorumSize` obrigatório. */
    QUORUM: 'QUORUM',
};
export const ApprovalDecision = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
};
/**
 * As 6 flags de ACL do legado (`erp_document_permission`), preservadas 1:1.
 * ged.md §5 — nomes originais: reader/writer/deleter/security/approver/download
 */
export const DocumentPermissionFlag = {
    /** Ver o documento e seus metadados. Legado: `reader` */
    READ: 'READ',
    /** Criar nova versão. Legado: `writer` */
    WRITE: 'WRITE',
    /** Excluir. Legado: `deleter` (nunca teve botão na UI do legado) */
    DELETE: 'DELETE',
    /** Baixar E imprimir o binário. Legado: `download` */
    DOWNLOAD: 'DOWNLOAD',
    /** Aprovar/reprovar versão. Legado: `approver` */
    APPROVE: 'APPROVE',
    /** Administrar o documento: obsoletar, reconfigurar distribuição. Legado: `security` */
    SECURITY: 'SECURITY',
};
export const ALL_DOCUMENT_PERMISSION_FLAGS = Object.values(DocumentPermissionFlag);
/**
 * Escopo de uma regra de ACL. No legado era codificado por sentinelas
 * (`department_id = 0`, `user_id = 0`) — ged.md §5.1. Aqui é explícito.
 */
export const AccessRuleScope = {
    /** Um colaborador específico. */
    USER: 'USER',
    /** Todos os membros de um departamento. */
    DEPARTMENT: 'DEPARTMENT',
    /** Todos da organização. */
    ORGANIZATION: 'ORGANIZATION',
};
/**
 * Efeito da regra. **Novidade em relação ao legado**, que não tinha negação:
 * as flags eram unidas por `MAX()` (OU lógico puro), então uma linha com
 * `reader = 0` não revogava nada (ged.md §5.3).
 *
 * DENY vence ALLOW e o escopo mais específico vence o mais amplo — ver
 * `@qualicloud/domain/acl`.
 */
export const AccessRuleEffect = {
    ALLOW: 'ALLOW',
    DENY: 'DENY',
};
/** Situação de validade, materializada — corrige a divergência de ged.md §9.3. */
export const ValidityState = {
    /** `validityMonths = 0` — documento sem prazo. */
    NOT_APPLICABLE: 'NOT_APPLICABLE',
    VALID: 'VALID',
    /** Dentro da janela de alerta (padrão 30 dias, configurável por organização). */
    EXPIRING_SOON: 'EXPIRING_SOON',
    EXPIRED: 'EXPIRED',
};
/** Estado da ciência (confirmação de leitura) de uma versão distribuída. */
export const AcknowledgementStatus = {
    PENDING: 'PENDING',
    ACKNOWLEDGED: 'ACKNOWLEDGED',
    /** Distribuição encerrada sem ciência (versão substituída antes da leitura). */
    SUPERSEDED: 'SUPERSEDED',
};
// ─────────────────────────────────────────────────────────────────────────────
// Assinatura eletrônica — 21 CFR Part 11 Subparte C
// ─────────────────────────────────────────────────────────────────────────────
/**
 * §11.50(a) — a assinatura precisa declarar o SIGNIFICADO.
 * O código é estável; o texto exibido é congelado no registro no momento do ato
 * (`meaning_text`), para não mudar retroativamente — §11.50(b).
 */
export const SignatureMeaning = {
    AUTHORED: 'AUTHORED',
    REVIEWED: 'REVIEWED',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    VERIFIED: 'VERIFIED',
};
/** Texto pt-BR padrão de cada significado. Congelado no ato — nunca lido de volta para exibir. */
export const SIGNATURE_MEANING_TEXT_PT_BR = {
    AUTHORED: 'Elaborado por',
    REVIEWED: 'Revisado por',
    APPROVED: 'Aprovado por',
    REJECTED: 'Reprovado por',
    VERIFIED: 'Verificado por',
};
/** Tipos de registro assináveis. Usado em `electronic_signature.record_type`. */
export const SignableRecordType = {
    DOCUMENT_VERSION: 'DOCUMENT_VERSION',
    NONCONFORMITY: 'NONCONFORMITY',
    NC_EVALUATION: 'NC_EVALUATION',
    CAPA_ACTION: 'CAPA_ACTION',
    EFFECTIVENESS_CHECK: 'EFFECTIVENESS_CHECK',
    MEETING: 'MEETING',
    CIRCULAR_ACKNOWLEDGEMENT: 'CIRCULAR_ACKNOWLEDGEMENT',
    DOCUMENT_ACKNOWLEDGEMENT: 'DOCUMENT_ACKNOWLEDGEMENT',
    RISK_MANAGEMENT_FILE: 'RISK_MANAGEMENT_FILE',
    MIGRATION_RECONCILIATION: 'MIGRATION_RECONCILIATION',
};
export const SignatureAuthMethod = {
    PASSWORD: 'PASSWORD',
    PASSWORD_MFA: 'PASSWORD_MFA',
};
export const SIGNABLE_ACTS = {
    DOCUMENT_APPROVE: {
        act: 'DOCUMENT_APPROVE',
        recordType: SignableRecordType.DOCUMENT_VERSION,
        meaning: SignatureMeaning.APPROVED,
        reasonRequired: false,
        labelPtBr: 'Aprovar versão de documento',
    },
    DOCUMENT_REJECT: {
        act: 'DOCUMENT_REJECT',
        recordType: SignableRecordType.DOCUMENT_VERSION,
        meaning: SignatureMeaning.REJECTED,
        reasonRequired: true,
        labelPtBr: 'Reprovar versão de documento',
    },
    DOCUMENT_OBSOLETE: {
        act: 'DOCUMENT_OBSOLETE',
        recordType: SignableRecordType.DOCUMENT_VERSION,
        meaning: SignatureMeaning.APPROVED,
        reasonRequired: true,
        labelPtBr: 'Obsoletar documento',
    },
    DOCUMENT_ACKNOWLEDGE: {
        act: 'DOCUMENT_ACKNOWLEDGE',
        recordType: SignableRecordType.DOCUMENT_ACKNOWLEDGEMENT,
        meaning: SignatureMeaning.REVIEWED,
        reasonRequired: false,
        labelPtBr: 'Confirmar ciência de documento',
    },
    NC_EVALUATE: {
        act: 'NC_EVALUATE',
        recordType: SignableRecordType.NC_EVALUATION,
        meaning: SignatureMeaning.REVIEWED,
        reasonRequired: true,
        labelPtBr: 'Avaliar procedência de não-conformidade',
    },
    NC_NOT_APPLICABLE: {
        act: 'NC_NOT_APPLICABLE',
        recordType: SignableRecordType.NC_EVALUATION,
        meaning: SignatureMeaning.REVIEWED,
        reasonRequired: true,
        labelPtBr: 'Registrar "não procede"',
    },
    NC_CLOSE: {
        act: 'NC_CLOSE',
        recordType: SignableRecordType.NONCONFORMITY,
        meaning: SignatureMeaning.APPROVED,
        reasonRequired: false,
        labelPtBr: 'Encerrar não-conformidade',
    },
    EFFECTIVENESS_VERIFY: {
        act: 'EFFECTIVENESS_VERIFY',
        recordType: SignableRecordType.EFFECTIVENESS_CHECK,
        meaning: SignatureMeaning.VERIFIED,
        reasonRequired: false,
        labelPtBr: 'Verificar eficácia de ação corretiva',
    },
    EFFECTIVENESS_REJECT: {
        act: 'EFFECTIVENESS_REJECT',
        recordType: SignableRecordType.EFFECTIVENESS_CHECK,
        meaning: SignatureMeaning.REJECTED,
        reasonRequired: true,
        labelPtBr: 'Reprovar eficácia de ação corretiva',
    },
    MEETING_CLOSE: {
        act: 'MEETING_CLOSE',
        recordType: SignableRecordType.MEETING,
        meaning: SignatureMeaning.AUTHORED,
        reasonRequired: false,
        labelPtBr: 'Encerrar reunião / emitir ata',
    },
    CIRCULAR_ACKNOWLEDGE: {
        act: 'CIRCULAR_ACKNOWLEDGE',
        recordType: SignableRecordType.CIRCULAR_ACKNOWLEDGEMENT,
        meaning: SignatureMeaning.REVIEWED,
        reasonRequired: false,
        labelPtBr: 'Confirmar leitura de circular',
    },
    RISK_FILE_APPROVE: {
        act: 'RISK_FILE_APPROVE',
        recordType: SignableRecordType.RISK_MANAGEMENT_FILE,
        meaning: SignatureMeaning.APPROVED,
        reasonRequired: false,
        labelPtBr: 'Aprovar arquivo de gestão de risco',
    },
};
// ─────────────────────────────────────────────────────────────────────────────
// Auditoria
// ─────────────────────────────────────────────────────────────────────────────
export const AuditAction = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    READ: 'READ',
    APPROVE: 'APPROVE',
    REJECT: 'REJECT',
    SIGN: 'SIGN',
    LOGIN: 'LOGIN',
    LOGIN_FAILED: 'LOGIN_FAILED',
    LOGOUT: 'LOGOUT',
    SESSION_EXPIRED: 'SESSION_EXPIRED',
    LOCKOUT: 'LOCKOUT',
    /**
     * Desbloqueio administrativo de conta.
     *
     * Membro próprio porque bloquear e desbloquear são atos com autores e
     * significados opostos: o bloqueio é automático (§11.300(d)), o desbloqueio é
     * decisão de um administrador. Emitir os dois como `LOCKOUT` obrigaria o
     * inspetor a inferir a direção a partir de `oldValue`/`newValue` — trilha que
     * exige interpretação não é trilha legível.
     */
    UNLOCK: 'UNLOCK',
    PASSWORD_CHANGE: 'PASSWORD_CHANGE',
    PASSWORD_RESET: 'PASSWORD_RESET',
    PERMISSION_GRANT: 'PERMISSION_GRANT',
    PERMISSION_REVOKE: 'PERMISSION_REVOKE',
    ROLE_CHANGE: 'ROLE_CHANGE',
    DOWNLOAD: 'DOWNLOAD',
    PRINT: 'PRINT',
    EXPORT: 'EXPORT',
    CONFIG_CHANGE: 'CONFIG_CHANGE',
    /** Acesso administrativo do fornecedor ao ambiente do cliente — plano §6.5, crítico. */
    VENDOR_ACCESS: 'VENDOR_ACCESS',
    /**
     * Carga de registro regulado vindo do sistema legado.
     *
     * RESERVADO PARA O ETL DA ONDA 4 — sem emissor em produção nesta onda,
     * declarado aqui de propósito. O ETL de migração (plano §10.4) precisa gravar
     * na trilha que uma linha nasceu de importação e não de ato de usuário, e o
     * membro do enum é parte do contrato que o ETL vai consumir: declará-lo depois
     * significaria mudar o enum com trilha já em produção.
     *
     * A ausência de emissor é escopo declarado, não defeito. Auditoria futura: não
     * reportar como "ação órfã" — o emissor é a ferramenta de ETL, executada uma
     * única vez por tenant no corte, no mesmo padrão de
     * `global-shared-lib/domain/src/legacy-map.ts`.
     */
    DATA_MIGRATION: 'DATA_MIGRATION',
    OBSOLETE: 'OBSOLETE',
    ACKNOWLEDGE: 'ACKNOWLEDGE',
};
// ─────────────────────────────────────────────────────────────────────────────
// Identidade e autorização
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Papéis declarativos. No legado nenhum papel era declarativo:
 * `user_group_id === 1 && store_id === 0` era admin global
 * (identity-authz-audit.md — `cart/user.php:350-357`).
 */
export const SystemRole = {
    /** Fornecedor (Riume). Acesso a QUALQUER organização — sempre gera evento VENDOR_ACCESS. */
    PLATFORM_ADMIN: 'PLATFORM_ADMIN',
    /** Admin da organização do cliente. */
    ORG_ADMIN: 'ORG_ADMIN',
    /** Gestor da qualidade — aprova, avalia NC, obsoleta. */
    QUALITY_MANAGER: 'QUALITY_MANAGER',
    /** Gerente de departamento. */
    DEPARTMENT_MANAGER: 'DEPARTMENT_MANAGER',
    /** Colaborador. */
    MEMBER: 'MEMBER',
    /** Auditor: leitura + trilha, sem escrita. */
    AUDITOR: 'AUDITOR',
};
/** Permissões funcionais (ABAC). Substituem `screens_json` e o JSON legado do OpenCart. */
export const Permission = {
    DOCUMENT_CREATE: 'document:create',
    DOCUMENT_READ_ALL: 'document:read:all',
    DOCUMENT_WRITE_ALL: 'document:write:all',
    DOCUMENT_APPROVE_ALL: 'document:approve:all',
    DOCUMENT_OBSOLETE: 'document:obsolete',
    DOCUMENT_TYPE_MANAGE: 'document-type:manage',
    /**
     * Administrar os catálogos do SGQ — tipos de reunião e de solicitação.
     *
     * Permissão PRÓPRIA, e não `MEETING_MANAGE` reaproveitada, porque o gerente de
     * setor tem `MEETING_MANAGE`: reusá-la daria a ele o poder de reescrever a
     * tabela de apoio da organização inteira, mudando a classificação de registros
     * já emitidos por outros setores.
     */
    SGQ_TYPE_MANAGE: 'sgq-type:manage',
    /**
     * Abrir solicitação. Existe porque a solicitação AVULSA não depende mais de
     * conhecer uma causa-raiz válida — antes, era esse conhecimento que fazia as
     * vezes de autorização. Sem uma permissão explícita, qualquer sessão
     * autenticada, inclusive a de AUDITOR, passaria a criar registro de qualidade.
     */
    REQUEST_CREATE: 'request:create',
    NC_CREATE: 'nc:create',
    NC_EVALUATE: 'nc:evaluate',
    NC_CLOSE: 'nc:close',
    MEETING_MANAGE: 'meeting:manage',
    TASK_VERIFY: 'task:verify',
    CIRCULAR_MANAGE: 'circular:manage',
    RISK_MANAGE: 'risk:manage',
    AUDIT_READ: 'audit:read',
    AUDIT_EXPORT: 'audit:export',
    ORG_MANAGE: 'org:manage',
    USER_MANAGE: 'user:manage',
    DEPARTMENT_MANAGE: 'department:manage',
    /*
     * ───────────────────────────────────────────────────────────────────────────
     * PERMISSÕES DE PLATAFORMA — o console do fornecedor.
     * ───────────────────────────────────────────────────────────────────────────
     *
     * São TRÊS permissões novas, e não `ORG_MANAGE` reaproveitada, por um motivo
     * que já apareceu neste arquivo com `SGQ_TYPE_MANAGE`: `ORG_MANAGE` pertence
     * ao `ORG_ADMIN` de cada cliente, que a usa para configurar a PRÓPRIA
     * organização. Reusá-la aqui daria a todo administrador de todo cliente o
     * poder de listar os demais clientes da plataforma, criar organizações e
     * elevar acesso de suporte — vazamento entre inquilinos concedido por
     * economia de enum.
     *
     * Também são três, e não uma, porque as consequências são de ordens
     * diferentes: ver a carteira de clientes é rotina comercial; entrar nos dados
     * de um cliente é ato que precisa de justificativa e prazo. Uma permissão só
     * impediria dar a primeira sem dar a segunda.
     */
    /** Ver a carteira de clientes e o volume de uso de cada um. Sem acesso a conteúdo. */
    PLATFORM_TENANT_READ: 'platform:tenant:read',
    /** Criar cliente, alterar configuração, suspender, reativar e mudar o contrato. */
    PLATFORM_TENANT_MANAGE: 'platform:tenant:manage',
    /** Elevar o próprio acesso à organização de um cliente — com justificativa e prazo. */
    PLATFORM_SUPPORT_ACCESS: 'platform:support:access',
};
/**
 * As permissões que pertencem AO FORNECEDOR e a mais ninguém.
 *
 * Existe para ser subtraída de `ORG_ADMIN` logo abaixo. Sem essa subtração,
 * `Object.values(Permission)` entregaria a carteira inteira de clientes ao
 * administrador de qualquer um deles — e o defeito nasceria calado, no dia em
 * que alguém acrescentasse uma permissão de plataforma ao enum.
 */
const PLATFORM_ONLY_PERMISSIONS = [
    Permission.PLATFORM_TENANT_READ,
    Permission.PLATFORM_TENANT_MANAGE,
    Permission.PLATFORM_SUPPORT_ACCESS,
];
/**
 * Papel → permissões. `PLATFORM_ADMIN` NÃO recebe permissões de negócio por padrão:
 * ele precisa de elevação explícita e auditada por organização (plano §6.5).
 */
export const ROLE_PERMISSIONS = {
    /*
     * `ORG_MANAGE` continua aqui, mas note o que ele SIGNIFICA para este papel:
     * configurar a organização em que a sessão está — que, para o administrador
     * de plataforma sem elevação, é a do próprio fornecedor. Não é uma chave para
     * a organização alheia; quem abre essa porta é `PLATFORM_SUPPORT_ACCESS`, com
     * justificativa e prazo.
     */
    PLATFORM_ADMIN: [
        Permission.ORG_MANAGE,
        Permission.AUDIT_READ,
        Permission.AUDIT_EXPORT,
        Permission.PLATFORM_TENANT_READ,
        Permission.PLATFORM_TENANT_MANAGE,
        Permission.PLATFORM_SUPPORT_ACCESS,
    ],
    /*
     * "Tudo o que existe DENTRO de uma organização" — e a subtração é o que torna
     * a frase verdadeira. `ORG_ADMIN` é o dono do SGQ do cliente, não um sócio da
     * plataforma.
     */
    ORG_ADMIN: Object.values(Permission).filter((permission) => !PLATFORM_ONLY_PERMISSIONS.includes(permission)),
    QUALITY_MANAGER: [
        Permission.DOCUMENT_CREATE,
        Permission.DOCUMENT_READ_ALL,
        Permission.DOCUMENT_WRITE_ALL,
        Permission.DOCUMENT_APPROVE_ALL,
        Permission.DOCUMENT_OBSOLETE,
        Permission.DOCUMENT_TYPE_MANAGE,
        Permission.SGQ_TYPE_MANAGE,
        Permission.REQUEST_CREATE,
        Permission.NC_CREATE,
        Permission.NC_EVALUATE,
        Permission.NC_CLOSE,
        Permission.MEETING_MANAGE,
        Permission.TASK_VERIFY,
        Permission.CIRCULAR_MANAGE,
        Permission.RISK_MANAGE,
        Permission.AUDIT_READ,
    ],
    DEPARTMENT_MANAGER: [
        Permission.DOCUMENT_CREATE,
        Permission.REQUEST_CREATE,
        Permission.NC_CREATE,
        Permission.NC_EVALUATE,
        Permission.MEETING_MANAGE,
        Permission.TASK_VERIFY,
    ],
    // O colaborador abre solicitação: no legado, `addSS` era alcançável por quem
    // tivesse o módulo de reunião, e pedir serviço é ato de rotina, não de gestão.
    MEMBER: [Permission.DOCUMENT_CREATE, Permission.REQUEST_CREATE, Permission.NC_CREATE],
    AUDITOR: [Permission.DOCUMENT_READ_ALL, Permission.AUDIT_READ, Permission.AUDIT_EXPORT],
};
// ─────────────────────────────────────────────────────────────────────────────
// SGQ — preservados do legado para o modelo de dados da Onda 2
// ─────────────────────────────────────────────────────────────────────────────
/** Corrige C10: no legado `status_nc='F'` era gravado na avaliação, antes de existir CAPA. */
export const NonconformityStatus = {
    OPEN: 'OPEN',
    UNDER_EVALUATION: 'UNDER_EVALUATION',
    NOT_APPLICABLE: 'NOT_APPLICABLE',
    ACCEPTED: 'ACCEPTED',
    IN_TREATMENT: 'IN_TREATMENT',
    PENDING_EFFECTIVENESS: 'PENDING_EFFECTIVENESS',
    CLOSED: 'CLOSED',
};
/** As 6 categorias clássicas do diagrama de Ishikawa (6M). */
export const IshikawaCategory = {
    METHOD: 'METHOD',
    MACHINE: 'MACHINE',
    MATERIAL: 'MATERIAL',
    MANPOWER: 'MANPOWER',
    MEASUREMENT: 'MEASUREMENT',
    ENVIRONMENT: 'ENVIRONMENT',
};
export const ISHIKAWA_CATEGORY_LABEL_PT_BR = {
    METHOD: 'Método',
    MACHINE: 'Máquina',
    MATERIAL: 'Material',
    MANPOWER: 'Mão de obra',
    MEASUREMENT: 'Medição',
    ENVIRONMENT: 'Meio ambiente',
};
/**
 * ORIGEM da não-conformidade.
 *
 * No legado **não existia campo de origem** em `erp_nc` (sgq.md §A.7): o bloco
 * `<select name="origemNC">` estava COMENTADO no formulário
 * (`view/template/qualidade/qualidade.twig:134-143`), e a origem era inferida do
 * `tipo` — um inteiro 1..25 hardcoded em três cópias divergentes (BUG-NC-07).
 * Inferir origem de tipo funciona até o cliente cadastrar um tipo novo; então a
 * inferência quebra em silêncio e o indicador de "NC por origem" — insumo da
 * análise crítica pela direção, ISO 13485 §5.6 — passa a mentir.
 *
 * Aqui a origem é campo próprio, obrigatório, com o conjunto derivado dos tipos
 * do legado listados em §A.7 (coluna "Origem implícita").
 */
export const NonconformitySource = {
    /** §A.7 ids 17, 19, 24 — "NC/Observação/Sugestão em Auditoria Interna". */
    INTERNAL_AUDIT: 'INTERNAL_AUDIT',
    /** §A.7 ids 16, 18, 23 — auditoria de certificação, cliente ou ANVISA. */
    EXTERNAL_AUDIT: 'EXTERNAL_AUDIT',
    /** §A.7 ids 4, 21, 22 — reclamação, defeito em campo, pesquisa de satisfação. */
    CUSTOMER: 'CUSTOMER',
    /** §A.7 ids 1,2,3,6,8,12,13,14,25 — desvio de processo interno. */
    PROCESS: 'PROCESS',
    /** Falha atribuída a insumo ou serviço de terceiro. */
    SUPPLIER: 'SUPPLIER',
    /** §A.7 ids 5, 11 — desvio de produto/produção. */
    PRODUCT: 'PRODUCT',
    /** §A.7 id 10 — evento adverso, queixa técnica. ISO 13485 §8.2.3. */
    REGULATORY: 'REGULATORY',
    /** §A.7 ids 15, 20 — indicador abaixo da meta, quase-acidente, 5S. */
    INTERNAL_MONITORING: 'INTERNAL_MONITORING',
};
export const NONCONFORMITY_SOURCE_LABEL_PT_BR = {
    INTERNAL_AUDIT: 'Auditoria interna',
    EXTERNAL_AUDIT: 'Auditoria externa',
    CUSTOMER: 'Cliente',
    PROCESS: 'Processo',
    SUPPLIER: 'Fornecedor',
    PRODUCT: 'Produto',
    REGULATORY: 'Regulatório',
    INTERNAL_MONITORING: 'Monitoramento interno',
};
/**
 * NATUREZA do registro.
 *
 * O legado misturava natureza e origem no mesmo inteiro `erp_nc.tipo`: os ids
 * 18/19 eram "Observação", 23/24 "Sugestão" e 16/17 "Não Conformidade" —
 * severidades regulatórias DIFERENTES tratadas pelo mesmo fluxo (sgq.md §A.7).
 * Separar importa: ISO 13485 §8.5.2 só exige ação corretiva para não-conformidade
 * real; observação e oportunidade de melhoria não deveriam bloquear indicador de
 * eficácia de CAPA.
 */
export const NonconformityType = {
    NONCONFORMITY: 'NONCONFORMITY',
    OBSERVATION: 'OBSERVATION',
    IMPROVEMENT_OPPORTUNITY: 'IMPROVEMENT_OPPORTUNITY',
    /** §A.7 id 10 — evento adverso / queixa técnica. Rastreio próprio. */
    ADVERSE_EVENT: 'ADVERSE_EVENT',
    /** §A.7 id 21 — reclamação de cliente. ISO 13485 §8.2.2. */
    COMPLAINT: 'COMPLAINT',
};
export const NONCONFORMITY_TYPE_LABEL_PT_BR = {
    /** Com hífen: é a grafia usada em título, botão, aviso e estado vazio de todas as telas. */
    NONCONFORMITY: 'Não-conformidade',
    OBSERVATION: 'Observação',
    IMPROVEMENT_OPPORTUNITY: 'Oportunidade de melhoria',
    ADVERSE_EVENT: 'Evento adverso / queixa técnica',
    COMPLAINT: 'Reclamação de cliente',
};
/**
 * Naturezas que EXIGEM tratamento CAPA para encerrar.
 *
 * Observação e oportunidade de melhoria podem ser encerradas por "não procede"
 * ou por decisão registrada sem plano de ação — ISO 13485 §8.5.2 fala de
 * não-conformidade, não de sugestão.
 */
export const CAPA_REQUIRED_NONCONFORMITY_TYPES = [
    NonconformityType.NONCONFORMITY,
    NonconformityType.ADVERSE_EVENT,
    NonconformityType.COMPLAINT,
];
/**
 * Tipo de reunião.
 *
 * A FLAG `SACAP` substitui o `LIKE '%SACAP%'` do legado.
 *
 * PARIDADE sgq.md §B.3 / §B.7: o status calculado `W` ("aguardando análise de
 * eficácia") dependia de `COALESCE(tipo_desc.name,'') LIKE '%SACAP%'`
 * (`model/reuniao/reuniao.php:417`) — casamento por NOME. Renomear o tipo para
 * "Reunião de Análise de Causa" apagava silenciosamente a exigência de avaliar a
 * eficácia. Pior: a busca do tipo em `createFollowUpSacap` consultava colunas
 * INEXISTENTES (BUG-REU-02) e a SACAP de follow-up nascia com `id_tipo = 0`, o
 * que fazia o `LIKE` nunca casar — a reunião gerada automaticamente nunca
 * chegava a `W`.
 */
/**
 * Códigos dos tipos de reunião SEMEADOS.
 *
 * Deixou de ser o universo válido: o catálogo agora é a tabela `meeting_type`,
 * administrável pela organização, e o cliente cria os tipos que quiser. Estes
 * três continuam existindo porque são semeados em toda organização e porque o
 * sistema precisa achar um deles pelo código — `SACAP` é o tipo que a reunião de
 * follow-up assume quando é gerada automaticamente.
 *
 * O que NÃO se faz mais é decidir comportamento comparando com estes valores.
 * Quem diz se a reunião exige não-conformidade ou avaliação de eficácia são as
 * flags copiadas do tipo para a própria linha da reunião.
 */
export const MeetingKind = {
    /** Solução e Análise de Causa de Ação Preventiva/Corretiva. */
    SACAP: 'SACAP',
    /** Análise crítica pela direção — ISO 13485 §5.6. */
    MANAGEMENT_REVIEW: 'MANAGEMENT_REVIEW',
    /** Reunião de rotina, sem exigência regulatória de eficácia. */
    ROUTINE: 'ROUTINE',
};
export const MEETING_KIND_LABEL_PT_BR = {
    SACAP: 'SACAP — análise de causa e plano de ação',
    MANAGEMENT_REVIEW: 'Análise crítica pela direção',
    ROUTINE: 'Reunião de rotina',
};
/**
 * @deprecated Sobrevive apenas para SEMEAR o catálogo: é o que define as flags
 * iniciais dos três tipos nativos. Nenhuma decisão em tempo de execução deve
 * passar por aqui — a reunião carrega `requiresEffectiveness` na própria linha,
 * congelada no agendamento. Consultar o código do tipo para decidir regra é
 * reencenar o BUG-REU-02 com outra sintaxe.
 */
export function meetingKindRequiresEffectiveness(kind) {
    return kind === MeetingKind.SACAP;
}
/** Códigos dos tipos de solicitação semeados — `erp_solicitacao.id_tipo`. */
export const RequestTypeCode = {
    CORRECTIVE: 'CORRECTIVE',
    PREVENTIVE: 'PREVENTIVE',
    IMPROVEMENT: 'IMPROVEMENT',
    /** O caso avulso: pedido de trabalho, sem causa-raiz e sem 5W2H completo. */
    SERVICE: 'SERVICE',
};
/**
 * A especificação dos tipos nativos, em UM lugar.
 *
 * A migration cria estas mesmas linhas em SQL e o seed as pressupõe. Manter a
 * lista aqui é o que permite ao serviço reconstruir o catálogo de uma
 * organização criada depois da migration — sem isso, o primeiro cliente novo
 * abriria o formulário de reunião com o combo vazio.
 */
export const SEED_MEETING_TYPES = [
    {
        code: MeetingKind.SACAP,
        name: 'SACAP — análise de causa e plano de ação',
        description: 'Reunião de solução e análise de causa. Exige não-conformidade vinculada e avaliação de eficácia depois do encerramento.',
        requiresNonconformity: true,
        requiresEffectiveness: true,
        sortOrder: 10,
    },
    {
        code: MeetingKind.MANAGEMENT_REVIEW,
        name: 'Análise crítica pela direção',
        description: 'Análise crítica do sistema da qualidade pela direção — ISO 13485 §5.6.',
        requiresNonconformity: false,
        requiresEffectiveness: false,
        sortOrder: 20,
    },
    {
        code: MeetingKind.ROUTINE,
        name: 'Reunião de rotina',
        description: 'Reunião sem exigência regulatória de vínculo ou de eficácia.',
        requiresNonconformity: false,
        requiresEffectiveness: false,
        sortOrder: 30,
    },
];
export const SEED_REQUEST_TYPES = [
    {
        code: RequestTypeCode.CORRECTIVE,
        name: 'Ação corretiva',
        description: 'Nasce da análise de causa de uma não-conformidade — ISO 13485 §8.5.2. Exige causa-raiz e plano 5W2H completo.',
        requiresRootCause: true,
        requiresFullPlan: true,
        preventiveDefault: false,
        sortOrder: 10,
    },
    {
        code: RequestTypeCode.PREVENTIVE,
        name: 'Ação preventiva',
        description: 'Trata causa potencial, antes da ocorrência — ISO 13485 §8.5.3.',
        requiresRootCause: false,
        requiresFullPlan: true,
        preventiveDefault: true,
        sortOrder: 20,
    },
    {
        code: RequestTypeCode.IMPROVEMENT,
        name: 'Melhoria',
        description: 'Oportunidade de melhoria identificada fora do tratamento de não-conformidade.',
        requiresRootCause: false,
        requiresFullPlan: true,
        preventiveDefault: false,
        sortOrder: 30,
    },
    {
        code: RequestTypeCode.SERVICE,
        name: 'Solicitação de serviço',
        description: 'Pedido avulso de execução. Dispensa "por quê" e "como": é pedido de trabalho, não plano de ação corretiva.',
        requiresRootCause: false,
        requiresFullPlan: false,
        preventiveDefault: false,
        sortOrder: 40,
    },
];
/**
 * Presença do participante — `erp_reuniao_usuario.confirma`.
 *
 * PARIDADE sgq.md §B.3, com um valor a menos: o legado tinha CINCO valores para
 * quatro conceitos, porque `addReuniao` gravava `confirma = ''` (string vazia) e
 * `createFollowUpSacap` gravava `0` para o MESMO estado "convidado, ainda não
 * respondeu" (BUG-REU-04). `''` caía no fallback do mapa de rótulos e a tela
 * exibia célula em branco.
 *
 * Definido em `@qualicloud/contracts` e reexportado por `@qualicloud/domain`:
 * o contrato é a fonte da verdade do valor persistido (as entidades o espelham), o domínio
 * consome. Duplicar a lista nos dois pacotes é como o legado mantinha três
 * cópias do catálogo de tipos de NC (BUG-NC-07).
 */
export const AttendanceStatus = {
    /** Legado: `''` e `0`, unificados. */
    INVITED: 'INVITED',
    /** Legado: `1`. */
    CONFIRMED: 'CONFIRMED',
    /** Legado: `2`. */
    DECLINED: 'DECLINED',
    /** Presença efetiva registrada na abertura da reunião. Não existia no legado. */
    ATTENDED: 'ATTENDED',
    /** Legado: `9` — removido da lista ao salvar, sem apagar a linha. */
    ABSENT: 'ABSENT',
};
export const ATTENDANCE_STATUS_LABEL_PT_BR = {
    INVITED: 'Convidado',
    CONFIRMED: 'Confirmado',
    DECLINED: 'Recusado',
    ATTENDED: 'Presente',
    ABSENT: 'Ausente',
};
/**
 * Estado EXPLÍCITO da ação corretiva (5W2H).
 *
 * No legado o estado da tarefa não era coluna: era derivado de
 * `dt_inicio`/`dt_fim`/`dt_cancela`/`analise_aprovada` por um `CASE` em SQL, e
 * havia DUAS versões divergentes do mesmo `CASE` (BUG-TAR-05) — a mesma ação
 * aparecia "Aguardando Análise" na grade de tarefas e "Finalizada" no relatório
 * da reunião.
 */
export const CapaActionStatus = {
    PLANNED: 'PLANNED',
    IN_PROGRESS: 'IN_PROGRESS',
    AWAITING_VERIFICATION: 'AWAITING_VERIFICATION',
    EFFECTIVE: 'EFFECTIVE',
    INEFFECTIVE: 'INEFFECTIVE',
    CANCELLED: 'CANCELLED',
};
export const CAPA_ACTION_STATUS_LABEL_PT_BR = {
    PLANNED: 'Em espera',
    IN_PROGRESS: 'Em andamento',
    AWAITING_VERIFICATION: 'Aguardando verificação de eficácia',
    EFFECTIVE: 'Concluída (eficaz)',
    INEFFECTIVE: 'Reprovada (ineficaz)',
    CANCELLED: 'Cancelada',
};
/**
 * Estado da circular.
 *
 * A circular do legado **não tinha status** (sgq.md §D.4): nascia publicada, era
 * imutável por AUSÊNCIA de endpoint de edição (BUG-CIR-07) e a única mutação
 * possível era acrescentar destinatários — sem validação nenhuma, por qualquer
 * usuário autenticado que soubesse o id (BUG-CIR-03). `DRAFT` existe para que um
 * erro de digitação seja corrigível ANTES de a circular virar evidência; depois
 * de `PUBLISHED` o texto é imutável e a correção é uma nova circular.
 */
export const CircularStatus = {
    DRAFT: 'DRAFT',
    PUBLISHED: 'PUBLISHED',
    ARCHIVED: 'ARCHIVED',
};
export const CIRCULAR_STATUS_LABEL_PT_BR = {
    DRAFT: 'Rascunho',
    PUBLISHED: 'Publicada',
    ARCHIVED: 'Arquivada',
};
export const NONCONFORMITY_STATUS_LABEL_PT_BR = {
    OPEN: 'Aberta',
    UNDER_EVALUATION: 'Em avaliação',
    NOT_APPLICABLE: 'Improcedente',
    ACCEPTED: 'Procedente',
    IN_TREATMENT: 'Em tratamento',
    PENDING_EFFECTIVENESS: 'Aguardando eficácia',
    CLOSED: 'Encerrada',
};
//# sourceMappingURL=enums.js.map